import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  getVersionLocalizations, 
  getScreenshots, 
  createScreenshotSet, 
  uploadScreenshot,
  deleteScreenshot,
  deleteScreenshotSet,
  reorderScreenshots
} from '../../services/api';

// Common locales for iOS apps (Apple App Store Connect format)
const AVAILABLE_LOCALES = [
  { code: 'ar-SA', name: 'Arabic' },
  { code: 'ca', name: 'Catalan' },
  { code: 'zh-Hans', name: 'Chinese (Simplified)' },
  { code: 'zh-Hant', name: 'Chinese (Traditional)' },
  { code: 'hr', name: 'Croatian' },
  { code: 'cs', name: 'Czech' },
  { code: 'da', name: 'Danish' },
  { code: 'nl-NL', name: 'Dutch' },
  { code: 'en-AU', name: 'English (Australia)' },
  { code: 'en-CA', name: 'English (Canada)' },
  { code: 'en-GB', name: 'English (U.K.)' },
  { code: 'en-US', name: 'English (U.S.)' },
  { code: 'fi', name: 'Finnish' },
  { code: 'fr-FR', name: 'French' },
  { code: 'fr-CA', name: 'French (Canada)' },
  { code: 'de-DE', name: 'German' },
  { code: 'el', name: 'Greek' },
  { code: 'he', name: 'Hebrew' },
  { code: 'hi', name: 'Hindi' },
  { code: 'hu', name: 'Hungarian' },
  { code: 'id', name: 'Indonesian' },
  { code: 'it', name: 'Italian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'ms', name: 'Malay' },
  { code: 'no', name: 'Norwegian' },
  { code: 'pl', name: 'Polish' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)' },
  { code: 'pt-PT', name: 'Portuguese (Portugal)' },
  { code: 'ro', name: 'Romanian' },
  { code: 'ru', name: 'Russian' },
  { code: 'sk', name: 'Slovak' },
  { code: 'es-MX', name: 'Spanish (Mexico)' },
  { code: 'es-ES', name: 'Spanish (Spain)' },
  { code: 'sv', name: 'Swedish' },
  { code: 'th', name: 'Thai' },
  { code: 'tr', name: 'Turkish' },
  { code: 'uk', name: 'Ukrainian' },
  { code: 'vi', name: 'Vietnamese' },
];

function ScreenshotManager({ versionId, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [localizations, setLocalizations] = useState([]);
  const [selectedLocale, setSelectedLocale] = useState('');
  const [screenshots, setScreenshots] = useState({});
  const [screenshotSets, setScreenshotSets] = useState({}); // Store screenshot set IDs
  const [pendingScreenshots, setPendingScreenshots] = useState({});
  const [uploading, setUploading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedDevices, setSelectedDevices] = useState(['APP_IPHONE_65', 'APP_IPAD_PRO_3GEN_129']); // Default devices
  const [collapsedDevices, setCollapsedDevices] = useState(new Set()); // Track collapsed sections

  // All available screenshot display types
  const allDisplayTypes = [
    // iPhone devices
    { 
      key: 'APP_IPHONE_35', 
      label: 'iPhone 3.5"', 
      size: '640 x 960',
      description: 'iPhone 4s',
      category: 'iPhone'
    },
    { 
      key: 'APP_IPHONE_40', 
      label: 'iPhone 4.0"', 
      size: '640 x 1136',
      description: 'iPhone SE (1st gen), iPhone 5',
      category: 'iPhone'
    },
    { 
      key: 'APP_IPHONE_47', 
      label: 'iPhone 4.7"', 
      size: '750 x 1334',
      description: 'iPhone 8, iPhone SE (2nd/3rd gen)',
      category: 'iPhone'
    },
    { 
      key: 'APP_IPHONE_55', 
      label: 'iPhone 5.5"', 
      size: '1242 x 2208',
      description: 'iPhone 8 Plus',
      category: 'iPhone'
    },
    { 
      key: 'APP_IPHONE_58', 
      label: 'iPhone 5.8"', 
      size: '1125 x 2436',
      description: 'iPhone X, XS, 11 Pro',
      category: 'iPhone'
    },
    { 
      key: 'APP_IPHONE_61', 
      label: 'iPhone 6.1"', 
      size: '828 x 1792',
      description: 'iPhone 11, XR',
      category: 'iPhone'
    },
    { 
      key: 'APP_IPHONE_65', 
      label: 'iPhone 6.5"', 
      size: '1242 x 2688',
      description: 'iPhone 14 Pro Max, 13 Pro Max, 12 Pro Max, 11 Pro Max, XS Max',
      category: 'iPhone'
    },
    { 
      key: 'APP_IPHONE_67', 
      label: 'iPhone 6.7"', 
      size: '1290 x 2796',
      description: 'iPhone 15 Pro Max, 15 Plus, 14 Pro Max, 14 Plus',
      category: 'iPhone'
    },
    // iPad devices
    { 
      key: 'APP_IPAD_97', 
      label: 'iPad 9.7"', 
      size: '1536 x 2048',
      description: 'iPad 9.7" (5th-9th gen)',
      category: 'iPad'
    },
    { 
      key: 'APP_IPAD_105', 
      label: 'iPad 10.5"', 
      size: '1668 x 2224',
      description: 'iPad Pro 10.5", iPad Air (3rd gen)',
      category: 'iPad'
    },
    { 
      key: 'APP_IPAD_PRO_129', 
      label: 'iPad Pro 12.9" (2nd Gen)', 
      size: '2048 x 2732',
      description: '2nd Gen',
      category: 'iPad'
    },
    { 
      key: 'APP_IPAD_PRO_3GEN_129', 
      label: 'iPad Pro 12.9" (3rd Gen+)', 
      size: '2064 x 2752',
      description: '3rd Gen and later',
      category: 'iPad'
    },
    { 
      key: 'APP_IPAD_PRO_3GEN_11', 
      label: 'iPad Pro 11"', 
      size: '1668 x 2388',
      description: 'iPad Pro 11" (all generations)',
      category: 'iPad'
    }
  ];

  // Get currently selected display types
  const displayTypes = allDisplayTypes.filter(dt => selectedDevices.includes(dt.key));

  useEffect(() => {
    if (versionId) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const locResponse = await getVersionLocalizations(versionId);
      if (locResponse.data.success) {
        setLocalizations(locResponse.data.localizations);
        
        // Load screenshots for the first available localization instead of hardcoding en-US
        if (locResponse.data.localizations.length > 0) {
          const firstLoc = locResponse.data.localizations[0];
          setSelectedLocale(firstLoc.attributes.locale);
          await loadScreenshots(firstLoc.id);
        }
      }
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadScreenshots = async (localizationId) => {
    try {
      const response = await getScreenshots(localizationId);
      console.log('Screenshots response:', response.data);
      if (response.data.success) {
        // Organize screenshots by display type
        const organized = {};
        const sets = {}; // Store set IDs
        const screenshotSets = response.data.screenshotSets || [];
        const included = response.data.included || [];
        
        console.log('Screenshot sets:', screenshotSets);
        console.log('Included screenshots:', included);
        
        // Collect all device types that have screenshot sets
        const existingDeviceTypes = [];
        
        screenshotSets.forEach(set => {
          const displayType = set.attributes?.screenshotDisplayType;
          const setId = set.id;
          
          // Store the set ID for this display type
          sets[displayType] = setId;
          
          // Track devices that have existing screenshot sets
          if (displayType) {
            existingDeviceTypes.push(displayType);
          }
          
          // Get screenshot IDs from the set's relationships
          const screenshotIds = set.relationships?.appScreenshots?.data?.map(item => item.id) || [];
          
          console.log(`Display type: ${displayType}, Set ID: ${setId}, Screenshot IDs:`, screenshotIds);
          
          // Find the actual screenshot objects from included array
          const setScreenshots = included.filter(
            item => item.type === 'appScreenshots' && screenshotIds.includes(item.id)
          ).map(screenshot => ({
            ...screenshot,
            screenshotSetId: setId
          }));
          
          console.log(`Screenshots for ${displayType}:`, setScreenshots);
          
          organized[displayType] = setScreenshots;
        });
        
        console.log('Organized screenshots:', organized);
        console.log('Screenshot sets map:', sets);
        console.log('Existing device types:', existingDeviceTypes);
        
        setScreenshots(organized);
        setScreenshotSets(sets); // Store the set IDs
        
        // Auto-select devices that have existing screenshot sets
        if (existingDeviceTypes.length > 0) {
          const defaultDevices = ['APP_IPHONE_65', 'APP_IPAD_PRO_3GEN_129'];
          
          setSelectedDevices(prev => {
            // Merge existing selections with newly found device types
            const merged = new Set([...prev, ...existingDeviceTypes]);
            return Array.from(merged);
          });
          
          // Auto-collapse non-default devices that were just loaded
          setCollapsedDevices(prev => {
            const newCollapsed = new Set(prev);
            existingDeviceTypes.forEach(deviceType => {
              if (!defaultDevices.includes(deviceType)) {
                newCollapsed.add(deviceType);
              }
            });
            return newCollapsed;
          });
          
          console.log('Auto-selected devices:', existingDeviceTypes);
        }
      }
    } catch (err) {
      console.error('Failed to load screenshots:', err);
    }
  };

  const handleLocaleChange = async (locale) => {
    setSelectedLocale(locale);
    const localization = localizations.find(loc => loc.attributes.locale === locale);
    if (localization) {
      await loadScreenshots(localization.id);
    }
  };

  const onDrop = useCallback(async (acceptedFiles, displayType) => {
    if (acceptedFiles.length === 0) return;

    const localization = localizations.find(loc => loc.attributes.locale === selectedLocale);
    if (!localization) {
      alert('Localization not found');
      return;
    }

    // Get or create screenshot set ID
    let screenshotSetId;
    
    // First, check if we have the set ID from loaded data
    if (screenshotSets[displayType]) {
      screenshotSetId = screenshotSets[displayType];
      console.log(`Using existing screenshot set ID for ${displayType}:`, screenshotSetId);
    } else {
      // If no set exists, check if there are existing screenshots with the ID
      const existingScreenshots = screenshots[displayType] || [];
      if (existingScreenshots.length > 0) {
        screenshotSetId = existingScreenshots[0].screenshotSetId;
        console.log(`Using screenshot set ID from existing screenshots:`, screenshotSetId);
      } else {
        // Only create a new set if none exists
        try {
          console.log(`Creating new screenshot set for ${displayType}...`);
          const setResponse = await createScreenshotSet(localization.id, displayType);
          if (setResponse.data.success) {
            screenshotSetId = setResponse.data.screenshotSet.id;
            console.log(`Created new screenshot set:`, screenshotSetId);
            // Store the new set ID
            setScreenshotSets(prev => ({ ...prev, [displayType]: screenshotSetId }));
          } else {
            alert('Failed to create screenshot set');
            return;
          }
        } catch (err) {
          alert('Failed to create screenshot set: ' + err.message);
          return;
        }
      }
    }

    // Add files to pending uploads
    const pending = { ...pendingScreenshots };
    if (!pending[displayType]) {
      pending[displayType] = [];
    }

    for (const file of acceptedFiles) {
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      pending[displayType].push({
        id: `pending-${Date.now()}-${Math.random()}`,
        file,
        previewUrl,
        fileName: file.name,
        screenshotSetId,
        status: 'pending'
      });
    }

    setPendingScreenshots(pending);
    setHasChanges(true);
  }, [localizations, selectedLocale, screenshots, screenshotSets, pendingScreenshots]);

  const removePending = (displayType, pendingId) => {
    const pending = { ...pendingScreenshots };
    if (pending[displayType]) {
      const item = pending[displayType].find(p => p.id === pendingId);
      if (item && item.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }
      pending[displayType] = pending[displayType].filter(p => p.id !== pendingId);
      if (pending[displayType].length === 0) {
        delete pending[displayType];
      }
    }
    setPendingScreenshots(pending);
    setHasChanges(Object.keys(pending).length > 0);
  };

  const handleDeviceToggle = (deviceKey) => {
    setSelectedDevices(prev => {
      if (prev.includes(deviceKey)) {
        // Don't allow removing if it's the last device
        if (prev.length === 1) {
          alert('You must have at least one device type selected');
          return prev;
        }
        // Remove collapsed state when unchecking
        setCollapsedDevices(prevCollapsed => {
          const newCollapsed = new Set(prevCollapsed);
          newCollapsed.delete(deviceKey);
          return newCollapsed;
        });
        return prev.filter(key => key !== deviceKey);
      } else {
        // Auto-collapse non-default devices when adding them
        const defaultDevices = ['APP_IPHONE_65', 'APP_IPAD_PRO_3GEN_129'];
        if (!defaultDevices.includes(deviceKey)) {
          setCollapsedDevices(prevCollapsed => new Set([...prevCollapsed, deviceKey]));
        }
        return [...prev, deviceKey];
      }
    });
  };

  const toggleCollapse = (deviceKey) => {
    setCollapsedDevices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(deviceKey)) {
        newSet.delete(deviceKey);
      } else {
        newSet.add(deviceKey);
      }
      return newSet;
    });
  };

  const saveScreenshots = async () => {
    const pendingItems = Object.entries(pendingScreenshots).flatMap(([displayType, items]) => 
      items.map(item => ({ ...item, displayType }))
    );

    if (pendingItems.length === 0) {
      alert('No screenshots to upload');
      return;
    }

    setUploading(true);
    let successCount = 0;
    let failCount = 0;

    try {
      for (const item of pendingItems) {
        try {
          console.log('Uploading screenshot:', item.fileName);
          
          const uploadResponse = await uploadScreenshot(
            item.screenshotSetId,
            item.file
          );

          if (uploadResponse.data.success) {
            console.log('Screenshot uploaded successfully:', item.fileName);
            successCount++;
          } else {
            console.error('Upload failed:', uploadResponse.data.error);
            failCount++;
          }
        } catch (err) {
          console.error('Upload error for', item.fileName, err);
          failCount++;
        }
      }

      // Clear pending screenshots
      Object.values(pendingScreenshots).forEach(items => {
        items.forEach(item => {
          if (item.previewUrl) {
            URL.revokeObjectURL(item.previewUrl);
          }
        });
      });
      setPendingScreenshots({});
      setHasChanges(false);

      // Reload screenshots
      const localization = localizations.find(loc => loc.attributes.locale === selectedLocale);
      if (localization) {
        await loadScreenshots(localization.id);
      }

      if (successCount > 0) {
        alert(`Successfully uploaded ${successCount} screenshot(s)${failCount > 0 ? `, ${failCount} failed` : ''}`);
        if (onUpdate) onUpdate();
      } else {
        alert('Failed to upload screenshots');
      }
    } catch (err) {
      alert('Upload error: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const cancelChanges = () => {
    if (!window.confirm('Discard all unsaved changes?')) {
      return;
    }

    // Clean up preview URLs
    Object.values(pendingScreenshots).forEach(items => {
      items.forEach(item => {
        if (item.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    });

    setPendingScreenshots({});
    setHasChanges(false);
  };

  const handleDelete = async (screenshotId, displayType) => {
    if (!window.confirm('Are you sure you want to delete this screenshot?')) {
      return;
    }

    try {
      const response = await deleteScreenshot(screenshotId);
      if (response.data.success) {
        // Update local state immediately
        const updated = { ...screenshots };
        if (updated[displayType]) {
          updated[displayType] = updated[displayType].filter(s => s.id !== screenshotId);
        }
        setScreenshots(updated);
        
        if (onUpdate) onUpdate();
        alert('Screenshot deleted successfully!');
      } else {
        alert('Failed to delete screenshot');
      }
    } catch (err) {
      alert('Failed to delete screenshot: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteSet = async (displayType) => {
    if (!window.confirm(`Are you sure you want to delete this device type? This will remove the screenshot set for ${displayType}.`)) {
      return;
    }

    const screenshotSetId = screenshotSets[displayType];
    if (!screenshotSetId) {
      alert('No screenshot set found for this device type');
      return;
    }

    try {
      const response = await deleteScreenshotSet(screenshotSetId);
      if (response.data.success) {
        // Remove from selected devices
        setSelectedDevices(prev => prev.filter(d => d !== displayType));
        
        // Remove from screenshots state
        const updated = { ...screenshots };
        delete updated[displayType];
        setScreenshots(updated);
        
        // Remove from screenshot sets state
        const updatedSets = { ...screenshotSets };
        delete updatedSets[displayType];
        setScreenshotSets(updatedSets);
        
        if (onUpdate) onUpdate();
        alert('Screenshot set deleted successfully!');
      } else {
        alert('Failed to delete screenshot set');
      }
    } catch (err) {
      alert('Failed to delete screenshot set: ' + (err.response?.data?.error || err.message));
    }
  };

  if (loading) {
    return (
      <div className="loading-inline">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>App Screenshots</span>
        {hasChanges && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              className="btn btn-secondary"
              onClick={cancelChanges}
              disabled={uploading}
              style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={saveScreenshots}
              disabled={uploading}
              style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
            >
              {uploading ? 'Uploading...' : 'Save Screenshots'}
            </button>
          </div>
        )}
      </div>

      {/* Device Selection */}
      <div className="form-group" style={{ 
        background: 'var(--bg-secondary)', 
        padding: '1rem', 
        borderRadius: '8px',
        marginBottom: '1.5rem' 
      }}>
        <label className="form-label" style={{ marginBottom: '0.75rem' }}>
          📱 Select Device Types to Manage
        </label>
        
        <div style={{ marginBottom: '1rem' }}>
          <strong style={{ fontSize: '0.9rem' }}>iPhone Devices</strong>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '0.5rem',
            marginTop: '0.5rem'
          }}>
            {allDisplayTypes.filter(dt => dt.category === 'iPhone').map(device => {
              const hasScreenshots = screenshotSets[device.key];
              const screenshotCount = (screenshots[device.key] || []).length;
              
              return (
                <label key={device.key} style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.5rem',
                  background: 'var(--bg-primary)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  border: selectedDevices.includes(device.key) ? '2px solid var(--primary)' : '1px solid var(--border-light)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectedDevices.includes(device.key)}
                      onChange={() => handleDeviceToggle(device.key)}
                      style={{ marginRight: '0.5rem' }}
                    />
                    <span style={{ fontSize: '0.85rem' }}>{device.label}</span>
                  </div>
                  {hasScreenshots && (
                    <span style={{ 
                      fontSize: '0.7rem',
                      padding: '0.15rem 0.4rem',
                      background: screenshotCount > 0 ? '#34c759' : '#ff9500',
                      color: 'white',
                      borderRadius: '10px',
                      fontWeight: '600'
                    }}>
                      {screenshotCount}
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        </div>

        <div>
          <strong style={{ fontSize: '0.9rem' }}>iPad Devices</strong>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '0.5rem',
            marginTop: '0.5rem'
          }}>
            {allDisplayTypes.filter(dt => dt.category === 'iPad').map(device => {
              const hasScreenshots = screenshotSets[device.key];
              const screenshotCount = (screenshots[device.key] || []).length;
              
              return (
                <label key={device.key} style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.5rem',
                  background: 'var(--bg-primary)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  border: selectedDevices.includes(device.key) ? '2px solid var(--primary)' : '1px solid var(--border-light)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectedDevices.includes(device.key)}
                      onChange={() => handleDeviceToggle(device.key)}
                      style={{ marginRight: '0.5rem' }}
                    />
                    <span style={{ fontSize: '0.85rem' }}>{device.label}</span>
                  </div>
                  {hasScreenshots && (
                    <span style={{ 
                      fontSize: '0.7rem',
                      padding: '0.15rem 0.4rem',
                      background: screenshotCount > 0 ? '#34c759' : '#ff9500',
                      color: 'white',
                      borderRadius: '10px',
                      fontWeight: '600'
                    }}>
                      {screenshotCount}
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        </div>

        <small className="text-secondary" style={{ display: 'block', marginTop: '0.75rem' }}>
          Selected {selectedDevices.length} device type{selectedDevices.length !== 1 ? 's' : ''}. 
          Apple requires at least one device type.
        </small>
      </div>

      <div className="form-group">
        <label className="form-label">Language/Region</label>
        <select
          className="form-select"
          value={selectedLocale}
          onChange={(e) => handleLocaleChange(e.target.value)}
          disabled={hasChanges}
        >
          {localizations.map((loc, index) => {
            const localeInfo = AVAILABLE_LOCALES.find(l => l.code === loc.attributes.locale);
            const displayName = localeInfo ? `${localeInfo.name} (${loc.attributes.locale})` : loc.attributes.locale;
            const isPrimary = index === 0; // First localization is considered primary
            return (
              <option key={loc.id} value={loc.attributes.locale}>
                {isPrimary ? '⭐ ' : ''}{displayName}{isPrimary ? ' - Primary' : ''}
              </option>
            );
          })}
        </select>
        {hasChanges && (
          <small style={{ color: '#ff9500' }}>
            ⚠️ Save or cancel changes before switching language
          </small>
        )}
      </div>

      {selectedDevices.map(deviceKey => {
        const displayType = allDisplayTypes.find(d => d.key === deviceKey);
        if (!displayType) return null;
        
        const isCollapsed = collapsedDevices.has(deviceKey);
        const isDefault = ['APP_IPHONE_65', 'APP_IPAD_PRO_3GEN_129'].includes(deviceKey);
        const screenshotCount = (screenshots[deviceKey] || []).length;
        
        // Color coding by device type
        const isIPad = displayType.category === 'iPad';
        const headerBg = 'var(--bg-secondary)';
        const borderColor = 'var(--primary)';
        
        return (
          <div key={displayType.key} style={{ marginBottom: '1.5rem' }}>
            {/* Collapsible Header */}
            <div 
              style={{ 
                padding: '1rem',
                background: headerBg,
                borderRadius: '8px 8px 0 0',
                border: `2px solid ${borderColor}`,
                borderBottom: isCollapsed ? `2px solid ${borderColor}` : 'none',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'all 0.2s ease'
              }}
            >
              <div 
                onClick={() => toggleCollapse(deviceKey)}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.75rem',
                  cursor: 'pointer',
                  flex: 1
                }}
              >
                <span style={{ fontSize: '1.2rem' }}>
                  {isCollapsed ? '▶' : '▼'}
                </span>
                <span style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                  {displayType.label}
                </span>
                <span style={{ 
                  fontSize: '0.75rem',
                  padding: '0.25rem 0.5rem',
                  background: 'var(--primary)',
                  color: 'white',
                  borderRadius: '12px',
                  fontWeight: '600'
                }}>
                  {isIPad ? '📱 iPad' : '📱 iPhone'}
                </span>
                <span className="text-secondary" style={{ fontSize: '0.85rem' }}>
                  {displayType.size}
                </span>
                {screenshotCount > 0 ? (
                  <span style={{ 
                    fontSize: '0.75rem',
                    padding: '0.25rem 0.5rem',
                    background: '#34c759',
                    color: 'white',
                    borderRadius: '12px',
                    fontWeight: '600'
                  }}>
                    {screenshotCount} screenshot{screenshotCount !== 1 ? 's' : ''}
                  </span>
                ) : (
                  <span style={{ 
                    fontSize: '0.75rem',
                    padding: '0.25rem 0.5rem',
                    background: '#ff3b30',
                    color: 'white',
                    borderRadius: '12px',
                    fontWeight: '600'
                  }}>
                    Empty set
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {screenshotCount === 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteSet(deviceKey);
                    }}
                    style={{
                      padding: '0.4rem 0.8rem',
                      background: '#ff3b30',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseOver={(e) => e.target.style.background = '#cc2e24'}
                    onMouseOut={(e) => e.target.style.background = '#ff3b30'}
                  >
                    🗑️ Delete Set
                  </button>
                )}
                <span 
                  onClick={() => toggleCollapse(deviceKey)}
                  className="text-secondary"
                  style={{ fontSize: '0.85rem', cursor: 'pointer' }}
                >
                  {isCollapsed ? 'Click to expand' : 'Click to collapse'}
                </span>
              </div>
            </div>
            
            {/* Collapsible Content */}
            {!isCollapsed && (
              <div style={{ 
                border: `2px solid ${borderColor}`,
                borderTop: 'none',
                borderRadius: '0 0 8px 8px',
                padding: '1.5rem',
                background: 'var(--bg-secondary)'
              }}>
                <ScreenshotSection
                  displayType={displayType}
                  screenshots={screenshots[displayType.key] || []}
                  screenshotSetId={screenshotSets[deviceKey]}
                  pendingScreenshots={pendingScreenshots[displayType.key] || []}
                  onDrop={(files) => onDrop(files, displayType.key)}
                  onDelete={(screenshotId) => handleDelete(screenshotId, displayType.key)}
                  onRemovePending={(pendingId) => removePending(displayType.key, pendingId)}
                  uploading={uploading}
                  onReorder={() => {
                    // Reload screenshots after reordering
                    const localization = localizations.find(loc => loc.attributes.locale === selectedLocale);
                    if (localization) {
                      loadScreenshots(localization.id);
                    }
                  }}
                />
              </div>
            )}
          </div>
        );
      })}

      <div className="alert alert-info" style={{ marginTop: '1.5rem' }}>
        <p style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>
          <strong>📝 Screenshot Requirements:</strong>
        </p>
        <ul style={{ fontSize: '0.875rem', paddingLeft: '1.25rem', margin: 0 }}>
          <li>iPhone 6.5": 1242 x 2688 pixels (portrait) or 2688 x 1242 (landscape)</li>
          <li>iPad Pro 12.9": 2064 x 2752 pixels (portrait) or 2752 x 2064 (landscape)</li>
          <li>Format: PNG or JPEG</li>
          <li>Color space: RGB</li>
          <li>Up to 10 screenshots per device type</li>
        </ul>
      </div>
    </div>
  );
}

function ScreenshotSection({ displayType, screenshots, screenshotSetId, pendingScreenshots, onDrop, onDelete, onRemovePending, uploading, onReorder }) {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [reorderedScreenshots, setReorderedScreenshots] = useState(screenshots);
  const [reordering, setReordering] = useState(false);
  
  // Update local state when screenshots prop changes
  useEffect(() => {
    setReorderedScreenshots(screenshots);
  }, [screenshots]);
  
  const totalCount = reorderedScreenshots.length + pendingScreenshots.length;
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg']
    },
    maxFiles: 10 - totalCount,
    disabled: totalCount >= 10 || uploading
  });

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const items = [...reorderedScreenshots];
    const draggedItem = items[draggedIndex];
    items.splice(draggedIndex, 1);
    items.splice(index, 0, draggedItem);
    
    setDraggedIndex(index);
    setReorderedScreenshots(items);
  };

  const handleDragEnd = async () => {
    setDraggedIndex(null);
    
    // Check if order actually changed
    const orderChanged = reorderedScreenshots.some((screenshot, index) => 
      screenshot.id !== screenshots[index]?.id
    );
    
    if (orderChanged && screenshotSetId) {
      setReordering(true);
      try {
        // Get the new order of screenshot IDs
        const screenshotIds = reorderedScreenshots.map(s => s.id);
        
        console.log('Reordering screenshots:', screenshotIds);
        const response = await reorderScreenshots(screenshotSetId, screenshotIds);
        
        if (response.data.success) {
          console.log('Screenshots reordered successfully');
          // Notify parent to reload
          if (onReorder) onReorder();
        } else {
          const errorMsg = response.data.error?.message || response.data.error || 'Failed to reorder';
          alert('Failed to reorder screenshots: ' + errorMsg);
          // Revert to original order
          setReorderedScreenshots(screenshots);
        }
      } catch (err) {
        console.error('Error reordering screenshots:', err);
        alert('Error reordering screenshots: ' + err.message);
        // Revert to original order
        setReorderedScreenshots(screenshots);
      } finally {
        setReordering(false);
      }
    }
  };

  return (
    <div>
      {/* Device description */}
      <p className="text-secondary" style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>
        {displayType.description}
      </p>

      {/* Existing Screenshots */}
      {reorderedScreenshots.length > 0 && (
        <div>
          <h4 className="text-secondary" style={{ fontSize: '0.9rem', marginBottom: '0.75rem' }}>
            Current Screenshots ({reorderedScreenshots.length})
            <span className="text-primary" style={{ fontSize: '0.75rem', marginLeft: '0.5rem' }}>
              💡 Drag to reorder
            </span>
          </h4>
          <div className="screenshot-preview">
            {reorderedScreenshots.map((screenshot, index) => {
              // Get image URL from screenshot
              const templateUrl = screenshot.attributes?.imageAsset?.templateUrl || '';
              const imageUrl = templateUrl
                .replace('{w}', '300')
                .replace('{h}', '600')
                .replace('{f}', 'png');
              
              const isDragging = draggedIndex === index;
              
              return (
                <div 
                  key={screenshot.id} 
                  className="screenshot-item"
                  draggable={!uploading && !reordering}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  style={{
                    opacity: isDragging ? 0.5 : 1,
                    cursor: (uploading || reordering) ? 'default' : 'move',
                    position: 'relative'
                  }}
                >
                  {/* Drag Handle Icon */}
                  {!uploading && !reordering && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      left: '8px',
                      background: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      zIndex: 10,
                      pointerEvents: 'none'
                    }}>
                      ⋮⋮
                    </div>
                  )}
                  
                  {imageUrl ? (
                    <img 
                      src={imageUrl} 
                      alt={`Screenshot ${index + 1}`}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div style={{ 
                    display: imageUrl ? 'none' : 'flex',
                    width: '100%', 
                    height: '100%', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    background: 'var(--bg-secondary)',
                    fontSize: '2rem'
                  }}>
                    📱
                  </div>
                  <div className="screenshot-label">#{index + 1}</div>
                  <button
                    className="screenshot-remove"
                    onClick={() => onDelete(screenshot.id)}
                    title="Delete screenshot"
                    disabled={uploading}
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Pending Screenshots */}
      {pendingScreenshots.length > 0 && (
        <div style={{ marginTop: screenshots.length > 0 ? '1.5rem' : 0 }}>
          <h4 style={{ fontSize: '0.9rem', color: '#ff9500', marginBottom: '0.75rem' }}>
            ⏳ Pending Upload ({pendingScreenshots.length})
          </h4>
          <div className="screenshot-preview">
            {pendingScreenshots.map((pending, index) => (
              <div key={pending.id} className="screenshot-item" style={{ border: '2px dashed #ff9500' }}>
                <img 
                  src={pending.previewUrl} 
                  alt={pending.fileName}
                />
                <div className="screenshot-label" style={{ background: '#ff9500' }}>
                  New #{screenshots.length + index + 1}
                </div>
                <button
                  className="screenshot-remove"
                  onClick={() => onRemovePending(pending.id)}
                  title="Remove from upload queue"
                  disabled={uploading}
                  style={{ background: '#ff9500' }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Area */}
      {totalCount < 10 && (
        <div 
          {...getRootProps()} 
          className="screenshot-upload"
          style={{
            borderColor: isDragActive ? 'var(--primary)' : 'var(--border-light)',
            background: isDragActive ? 'var(--bg-tertiary)' : 'transparent',
            marginTop: (screenshots.length > 0 || pendingScreenshots.length > 0) ? '1.5rem' : 0,
            opacity: uploading ? 0.5 : 1,
            cursor: uploading ? 'not-allowed' : 'pointer'
          }}
        >
          <input {...getInputProps()} />
          <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>📸</div>
          {uploading ? (
            <p>Uploading...</p>
          ) : isDragActive ? (
            <p>Drop screenshots here</p>
          ) : (
            <>
              <p>Drag & drop screenshots here, or click to select</p>
              <small className="text-secondary">
                {totalCount}/10 screenshots • {10 - totalCount} remaining
              </small>
            </>
          )}
        </div>
      )}

      {totalCount >= 10 && (
        <div style={{
          padding: '1rem',
          background: 'var(--bg-secondary)',
          borderRadius: '8px',
          textAlign: 'center',
          marginTop: '1rem'
        }}>
          <p className="text-secondary" style={{ margin: 0 }}>
            Maximum 10 screenshots reached for this device type
          </p>
        </div>
      )}
    </div>
  );
}

export default ScreenshotManager;
